import { useTranslations } from "@acto/web-common/locales";
import { TeamMeetingState } from "@acto/gql/graphql";
import { MANAGEMENT_TODO_TYPES } from "../../../utils/managementTodoTypes";
import { PeriodOption, DateRange } from "../../DateRangeSelector/types";
import { periodToDateRange } from "../../../utils/periodToDateRange";
import { usePeriodLabel } from "../../DateRangeSelector/DateRangeSelector";
import { useSingleEmployee } from "@acto/taskpane/core/data-access/team/useSingleEmployee";
import {
  type AppointmentStateItem,
  useAppointmentStateData,
} from "@acto/taskpane/core/data-access/team/useAppointmentStateData";
import { CompletedAppointmentsSidePanel } from "../CompletedAppointmentsSidePanel/CompletedAppointmentsSidePanel";
import { CompletedAppointmentsSidePanelSkeleton } from "../CompletedAppointmentsSidePanel/CompletedAppointmentsSidePanelSkeleton";

interface EmployeeAppointmentStateSidePanelProps {
  state: TeamMeetingState;
  employeeId: string;
  employeeName: string;
  selectedPeriod?: PeriodOption;
  selectedCustomDateRange?: DateRange;
  onAppointmentClick?: (noteId: string | null, clientId: string) => void;
  onCustomerClick?: (customerId: string, customerName: string) => void;
}

export const EmployeeAppointmentStateSidePanel = ({
  state,
  employeeId,
  employeeName,
  selectedPeriod = PeriodOption.LAST_MONTH,
  selectedCustomDateRange,
  onAppointmentClick,
  onCustomerClick,
}: EmployeeAppointmentStateSidePanelProps) => {
  const t = useTranslations("managementView.appointmentStates");
  const effectiveDateRange = periodToDateRange(selectedPeriod, selectedCustomDateRange);
  const periodLabel = usePeriodLabel(selectedPeriod, selectedCustomDateRange);

  const { employee, isPending, isFetching } = useSingleEmployee({
    employeeId,
    dateRange: effectiveDateRange,
    todoTypes: MANAGEMENT_TODO_TYPES,
    withAppointments: true,
  });

  const data = useAppointmentStateData(employee?.appointments ?? null, state, employeeName, periodLabel);

  if (isPending || isFetching) {
    return <CompletedAppointmentsSidePanelSkeleton />;
  }

  if (!data) {
    return null;
  }

  const describe = (item: AppointmentStateItem): string => {
    if (!item.cancellation) return item.description;
    const reason = t(`reason.${item.cancellation.reason}`);
    return item.cancellation.comment ? `${reason} · ${item.cancellation.comment}` : reason;
  };

  const copy = {
    [TeamMeetingState.Planned]: { heading: t("planned.title"), countSuffix: t("planned.count") },
    [TeamMeetingState.NoReport]: { heading: t("noReport.title"), countSuffix: t("noReport.count") },
    [TeamMeetingState.Documented]: { heading: t("documented.title"), countSuffix: t("documented.count") },
    [TeamMeetingState.DidNotTakePlace]: {
      heading: t("didNotTakePlace.title"),
      countSuffix: t("didNotTakePlace.count"),
    },
  }[state];

  return (
    <CompletedAppointmentsSidePanel
      heading={copy.heading}
      countSuffix={copy.countSuffix}
      dateColor={state === TeamMeetingState.NoReport ? "error" : "default"}
      data={{
        entityName: data.employeeName,
        dateRange: data.period,
        completedCount: data.count,
        appointments: data.items.map((item) => ({
          id: item.id,
          date: item.date,
          title: item.clientName,
          clientExternalReference: item.clientExternalReference,
          todoCount: item.openTodosCount,
          description: describe(item),
          clientId: item.clientId,
          clientAccessible: item.clientAccessible,
          noteId: item.noteId,
          fromSignal: item.fromSignal,
        })),
      }}
      onAppointmentClick={onAppointmentClick}
      onCustomerClick={onCustomerClick}
    />
  );
};
